import { ApplicationState } from "@ppb/tbd-store/state/ApplicationState.types";
import { BetslipApplicationState } from "../store/state/BetslipApplicationState";
import {
  BettingMetadata,
  RunnersMetadata,
  SportsbookBettingRunnerData,
  BettingGenericMetadata,
  BettingRacingMetadata,
} from "@ppb/tbd-store/state/betting/sportsbook-betting/SportsbookBetting.types";
import { FallbackIconType, AlertType, AlertProps } from "@ppb/the-wall-common/types";
import type { Region } from "@ppb/the-wall-icons/traps";
import {
  BettingState,
  INVALID_SGM_COMBINATIONS_RUNNER_FAILURE_CODES,
  RUNNER_FAILURE_CODES,
  BET_TYPES,
} from "@ppb/betslip-core";
import {
  createSportsbookBettingRunnerSelector,
  getAllUniqueCombinationsFailures,
  getAllUniqueRunnersFailures,
  getSportsbookBettingNumRunners,
  getOperationalFailure,
  getSportsbookBettingImplyRunnerFailures,
  getSportsbookBettingRunners,
} from "../store/state/sportsbook-betting/sportsbook-betting-selectors";
import {
  getSbkPlaceImportantErrorCode,
  hasAnyMarketClosedFailure,
  hasAnyMarketSuspendedFailure,
  hasAnyInvalidCombinationFailure,
  isSingleLike,
} from "@ppb/tbd-store/helpers/sportsbook-betting";
import { formatOdds, formatDateWithOrdinal, formatTime, formatHandicap, formatRunnerName } from "@ppb/formatters";
import { getHasSportsbookTechnicalError } from "../store/state/betslip-module/betslip-module-selectors";
import { createSelector } from "reselect";
import { NotificationCode } from "./betslip-notification-code";
import { currencyFormatWithDecimalPlaces } from "../formatters/currency-adapter";
import { i18n } from "../helpers/i18n";
import { buildCombinationOdds } from "./betslip-formatters";
import { stripGreyhoundTrapPrefix } from "../helpers/greyhound-trap-icon-helper";
import { EventType, RacingSport } from "../constants";
import { getRaceSport, getSilkFallbackType } from "../helpers/race";
import type { SportsbookObbPlacePanelNotifications } from "./SportsbookPlace/snowflakes/SportsbookPlacePanel/SportsbookObbPlacePanel.types";
import { BetSelection } from "./SportsbookReceipt/snowflakes/SportsbookReceiptPanel/snowflakes/BetSelections/BetSelections.types";
import type { BetslipLocalization, BetslipOddsDisplay, SportsbookBetslipCapabilities } from "../contract";

type SelectionDescription = {
  title: string;
  name: string;
  subtitle: string;
  eventUrn: string;
  competitionUrn?: string;
  handicap?: string;
  racingSport?: RacingSport;
  icon?: string;
  silkFallbackType?: FallbackIconType;
  meetingCountry?: Region;
  trap?: number | string;
};

export type Combination = {
  id: string;
  creationTimestamp: number;
  legId: string;
  odds: string;
  previousOdds?: string;
  returnsLabel: string;
  stake?: number;
  silkIcon?: string;
  silkFallbackIconType?: FallbackIconType;
  selection: {
    title: string;
    subtitle: string;
  };
  isBonusAvailable: boolean;
  isEachWayAvailable: boolean;
  isEachWaySelected: boolean;
  eachWaySubtitle?: string;
  multiplier?: string;
};

export type BetBuilder = {
  id: string;
  betType: string;
  subtitle: string;
  selections: BetSelection[];
  selectionsLabel: string;
};

export const buildLotterySelection = <T extends SportsbookBettingRunnerData>(
  metadata: BettingGenericMetadata,
  localization: BetslipLocalization,
  runner?: T,
  drawCounter = 1,
): SelectionDescription => {
  const { eventName, runnerName, eventUrn, competitionName, eventOpenDate, competitionUrn } = metadata;
  const runnerHandicap = runner?.handicap ? runner?.handicap : undefined;
  const { localeCodeBcp47, timezone } = localization;

  const drawsLabel = i18n({ key: "I18N.LOTTERIES.DRAWS" });
  const translations = {
    other: i18n({ key: "I18N.LOTTERIES.DATE_ABBREVIATION_OTHER" }),
    first: i18n({ key: "I18N.LOTTERIES.DATE_ABBREVIATION_FIRST" }),
    second: i18n({ key: "I18N.LOTTERIES.DATE_ABBREVIATION_SECOND" }),
    third: i18n({ key: "I18N.LOTTERIES.DATE_ABBREVIATION_THIRD" }),
  };

  const dateFormated = eventOpenDate
    ? formatDateWithOrdinal(eventOpenDate, localeCodeBcp47, timezone, translations, true)
    : "";

  const subtitle =
    drawCounter > 1
      ? `${competitionName} (x${drawCounter} ${drawsLabel})`
      : `${competitionName} ${eventName} ${dateFormated}`.trim();

  return {
    title: formatRunnerName(runnerName, runnerHandicap),
    name: runnerName,
    handicap: formatHandicap(runnerHandicap),
    subtitle,
    eventUrn,
    competitionUrn,
  };
};

const buildGenericSelection = <T extends SportsbookBettingRunnerData>(
  metadata: BettingGenericMetadata,
  runner?: T,
  options?: { hideEventName?: boolean },
): SelectionDescription => {
  const { marketName, eventName, runnerName, eventUrn, isSuperSub } = metadata;
  const runnerHandicap = runner?.handicap ? runner?.handicap : undefined;

  // Bet builder and multi bet builder selections are already grouped under their event, so the subtitle omits it.
  let subtitle = options?.hideEventName ? marketName : `${marketName} - ${eventName}`;

  if (isSuperSub && !options?.hideEventName) {
    subtitle = i18n({
      key: "I18N.BETSLIP.SUPER_SUB.SUBTITLE",
      interpolationValues: { marketName, eventName },
    });
  }

  return {
    title: formatRunnerName(runnerName, runnerHandicap),
    name: runnerName,
    handicap: formatHandicap(runnerHandicap),
    subtitle,
    eventUrn,
  };
};

export const buildMultiBetBuilderGroup = (
  metadata: BettingMetadata,
  localization: BetslipLocalization,
): { groupUrn: string; groupTitle: string } => {
  if (metadata.type === "GENERIC") {
    const { eventUrn, eventName } = metadata;

    return {
      groupUrn: eventUrn,
      groupTitle: eventName,
    };
  }

  const { racing } = metadata;
  const { localeCodeBcp47, timezone } = localization;
  const raceTime = formatTime(racing.time, localeCodeBcp47, timezone);

  return {
    groupUrn: racing.urn,
    groupTitle: `${raceTime} ${racing.venue}`,
  };
};

const buildRacingSelection = (
  metadata: BettingRacingMetadata,
  localization: BetslipLocalization,
  options?: { hideEventName?: boolean },
): SelectionDescription => {
  const { racing, runnerName, marketTypeName, marketName, sportId, marketType } = metadata;
  const { localeCodeBcp47, timezone } = localization;

  const finalMarketTypeName = marketType === "BETTING_W/O" ? marketName : marketTypeName || marketName;
  const raceTime = formatTime(racing.time, localeCodeBcp47, timezone);
  const saddleCloth = racing.saddleCloth ? `${racing.saddleCloth} ` : "";
  const rawName = saddleCloth + runnerName;

  const getRegionFromCountry = (country: string): Region => {
    const regionMap: Record<string, Region> = {
      US: "US",
      GB: "UK",
      AU: "AU",
    };

    return regionMap[country] || "AGNOSTIC";
  };

  const getTrapIcon = (trap?: number, market?: string): string | number | undefined => {
    if (market === "WIN" || market === "BETTING_W/O") {
      return trap ?? "default";
    }
    return undefined;
  };

  const racingSport = getRaceSport(sportId);
  const trap = getTrapIcon(racing.trap, marketType);
  // `racing.trap` is nulled by the metadata selector when the trapIcon capability is disabled, so this only fires when the badge will render.
  const name =
    racingSport === RacingSport.GREYHOUND_RACING && typeof trap === "number"
      ? stripGreyhoundTrapPrefix(rawName)
      : rawName;

  // Bet builder and multi bet builder selections are already grouped under their race, so the subtitle omits the race time/venue.
  const subtitle = options?.hideEventName
    ? finalMarketTypeName
    : `${finalMarketTypeName} - ${raceTime} ${racing.venue}`;

  return {
    title: name,
    name,
    subtitle,
    racingSport,
    icon: racing.runnerVisual,
    silkFallbackType: getSilkFallbackType(sportId),
    meetingCountry: racing.meetingCountry ? getRegionFromCountry(racing.meetingCountry) : "AGNOSTIC",
    trap,
    eventUrn: racing.urn,
  };
};

export const buildSelection = <T extends SportsbookBettingRunnerData>(
  metadata: BettingMetadata,
  localization: BetslipLocalization,
  runner?: T,
  drawCounter = 1,
  options?: { hideEventName?: boolean },
): SelectionDescription => {
  if (metadata?.type === "RACING") {
    return buildRacingSelection(metadata, localization, options);
  }

  if (metadata.sportId === EventType.LOTTERIES) {
    return buildLotterySelection(metadata, localization, runner, drawCounter);
  }

  return buildGenericSelection(metadata, runner, options);
};

export const hasSGMFailures = (runnerFailures: BettingState.ImplyRunnerFailure[]): boolean =>
  runnerFailures.some((failure) =>
    [...INVALID_SGM_COMBINATIONS_RUNNER_FAILURE_CODES, RUNNER_FAILURE_CODES.NOT_ELIGIBLE_SGM_SELECTION].includes(
      failure.failureCode,
    ),
  );

export const hasAnyInvalidSGMCombinationFailure = (runnerFailures: BettingState.ImplyRunnerFailure[] = []): boolean =>
  runnerFailures.some((failure) => failure.failureCode === RUNNER_FAILURE_CODES.INVALID_SGM_COMBINATION);

export const hasAnySameMarketCombinationFailure = (runnerFailures: BettingState.ImplyRunnerFailure[] = []): boolean =>
  runnerFailures.some((failure) => failure.failureCode === RUNNER_FAILURE_CODES.IMPOSSIBLE_SAME_MARKET_COMBINATION);

export const hasNotEligibleSGMSelectionCombinationFailure = (
  runnerFailures: BettingState.ImplyRunnerFailure[] = [],
): boolean => runnerFailures.some((failure) => failure.failureCode === RUNNER_FAILURE_CODES.NOT_ELIGIBLE_SGM_SELECTION);

const checkAnyFailures = (
  failedRunners: BettingState.ImplyRunnerFailuresMap,
  checkFailure: (runnerFailures: BettingState.ImplyRunnerFailure[]) => boolean,
): boolean => Object.values(failedRunners).some((runnerFailures) => checkFailure(runnerFailures));

export const createMultiplesNotificationsSelector = () => {
  const noNotifications: SportsbookObbPlacePanelNotifications = [];
  const invalidCombinations: SportsbookObbPlacePanelNotifications = [
    {
      id: NotificationCode.NotCombinable,
      type: AlertType.Warning,
      message: i18n({ key: "I18N.BETSLIP.MAX_ONE_PER_EVENT_ERROR" }),
      detail: i18n({ key: "I18N.BETSLIP.MAX_ONE_PER_EVENT_ERROR_DETAILS" }),
    },
  ];

  return createSelector([getSportsbookBettingImplyRunnerFailures], (failedRunners) => {
    const hasAnyInvalidCombinationFailures = checkAnyFailures(failedRunners, hasAnyInvalidCombinationFailure);

    if (hasAnyInvalidCombinationFailures) {
      return invalidCombinations;
    }

    return noNotifications;
  });
};

export const createAvailabilityNotificationSelector = () =>
  createSelector(
    [
      getSportsbookBettingImplyRunnerFailures,
      (_state: ApplicationState, capabilities: SportsbookBetslipCapabilities) =>
        capabilities.marketAvailabilityGuidance.enabled,
      getSportsbookBettingNumRunners,
    ],
    (
      failedRunners: BettingState.ImplyRunnerFailuresMap,
      hasMarketAvailabilityGuidance: boolean,
      numRunners: number,
    ) => {
      const notification: AlertProps = {
        id: NotificationCode.Availability,
        type: AlertType.Info,
      };

      const availabilityFailures = Object.values(failedRunners).map((runnerFailures) => ({
        closed: hasAnyMarketClosedFailure(runnerFailures),
        suspended: hasAnyMarketSuspendedFailure(runnerFailures),
      }));
      const hasAnyMarketClosed = availabilityFailures.some(({ closed }) => closed);
      const hasAnyMarketSuspended = availabilityFailures.some(({ suspended }) => suspended);
      const numUnavailableRunners = availabilityFailures.filter(({ closed, suspended }) => closed || suspended).length;

      if (hasMarketAvailabilityGuidance && numRunners > 1 && numUnavailableRunners < numRunners) {
        if (hasAnyMarketClosed && hasAnyMarketSuspended) {
          return {
            ...notification,
            message: i18n({ key: "I18N.BETSLIP.SBG.AVAILABILITY_CHANGED" }),
            detail: i18n({ key: "I18N.BETSLIP.SBG.EXCLUDE_CLOSED_OR_SUSPENDED" }),
          };
        }

        if (hasAnyMarketSuspended || hasAnyMarketClosed) {
          return {
            ...notification,
            message: i18n({ key: "I18N.BETSLIP.SBG.AVAILABILITY_CHANGED" }),
            detail: i18n({ key: "I18N.BETSLIP.SBG.EXCLUDE_UNAVAILABLE" }),
          };
        }
      }

      if (hasAnyMarketClosed && hasAnyMarketSuspended) {
        return {
          ...notification,
          message: i18n({ key: "I18N.BETSLIP.AVAILABILITY_NOTIFICATION" }),
        };
      }

      if (hasAnyMarketClosed) {
        return {
          ...notification,
          message: i18n({ key: "I18N.BETSLIP.SBK.MARKET_CLOSED" }),
          detail: i18n({ key: "I18N.BETSLIP.CLOSED_MARKET_DETAILS" }),
        };
      }

      if (hasAnyMarketSuspended) {
        return {
          ...notification,
          message: i18n({ key: "I18N.BETSLIP.SBK.MARKET_SUSPENDED" }),
          detail: i18n({ key: "I18N.BETSLIP.SUSPENDED_MARKET_DETAILS" }),
        };
      }

      return undefined;
    },
  );

export const buildSportsbookTransactionalError = (appState: BetslipApplicationState): AlertProps | undefined => {
  const notification = { id: NotificationCode.TransactionalError, type: AlertType.Error };

  const operationalFailure = getOperationalFailure(appState);
  const hasSportsbookTechnicalError = getHasSportsbookTechnicalError(appState);

  if (hasSportsbookTechnicalError) {
    return {
      ...notification,
      message: i18n({ key: "I18N.BETSLIP.ERROR.UNKNOWN" }),
    };
  }

  if (!operationalFailure) {
    return undefined;
  }

  const messageCode = getSbkPlaceImportantErrorCode({
    operationalFailure,
    uniqueRunnersFailures: getAllUniqueRunnersFailures(appState),
    uniqueCombinationsFailures: getAllUniqueCombinationsFailures(appState),
  });
  return {
    ...notification,
    message: i18n({ key: `I18N.BETSLIP.SBK.ERROR.${messageCode}` }),
  };
};

export const buildReturnsLabel = ({
  odds,
  potentialReturns,
  localization,
  originalPotentialReturns,
  isPriceBoostSelected,
  hasStartingPrice,
}: {
  odds: BettingState.Odds | null;
  potentialReturns: number | null;
  localization: BetslipLocalization;
  originalPotentialReturns?: number | null;
  isPriceBoostSelected?: boolean;
  hasStartingPrice?: boolean;
}): string => {
  if (hasStartingPrice || (!potentialReturns && !odds)) {
    return i18n({ key: "I18N.BETSLIP.BET_RETURNS_TBD" });
  }

  const formattedPotential = currencyFormatWithDecimalPlaces({
    ...localization,
    value: potentialReturns || 0,
    decimalPlaces: 2,
  });

  if (originalPotentialReturns && isPriceBoostSelected) {
    const formattedOriginalPotential = currencyFormatWithDecimalPlaces({
      ...localization,
      value: originalPotentialReturns,
      decimalPlaces: 2,
    });

    return i18n({
      key: "I18N.BETSLIP.BET_RETURNS_WITH_ORIGINAL",
      interpolationValues: {
        potentialReturns: formattedPotential,
        originalPotentialReturns: formattedOriginalPotential,
      },
    });
  }

  return i18n({ key: "I18N.BETSLIP.BET_RETURNS", interpolationValues: { potentialReturns: formattedPotential } });
};

export const buildSingles = (
  state: BettingState.Group,
  localization: BetslipLocalization,
  bettingRunnersMetadata: RunnersMetadata,
  oddsDisplay: BetslipOddsDisplay,
): Combination[] => {
  const { combinations, legs } = state;
  const combinationsList = Object.values(combinations);
  const getSportsbookBettingRunner = createSportsbookBettingRunnerSelector();

  return combinationsList
    .filter((combination) => isSingleLike(combination, legs))
    .sort((combinationA, combinationB) => combinationA.creationTimestamp - combinationB.creationTimestamp)
    .map((combination) => {
      // Retrieving the first leg based on the assumption we only handle SIMPLE_SELECTION SINGLE's
      const legId = combination.legs[0];
      const { runners } = legs[legId];
      const [legRunnerId] = runners;
      const metadata = bettingRunnersMetadata[legRunnerId];
      const [previousOdds] = metadata.previousOdds || [];
      const bettingRunner = getSportsbookBettingRunner(state.runners, legRunnerId);
      const { title, subtitle, icon, silkFallbackType } = buildSelection(metadata, localization, bettingRunner);
      const {
        isEachWayAvailable,
        isEachWaySelected,
        eachWayPlaces,
        eachWayPlacesFraction,
        isPriceBoostAvailable,
        isPriceBoostSelected,
        odds,
        potentialReturns,
      } = combination;

      const eachWaySubtitle = isEachWayAvailable
        ? i18n({
            key: "I18N.BETSLIP.EACHWAY_ODDS_PLACES",
            interpolationValues: {
              numerator: eachWayPlacesFraction?.numerator || "",
              denominator: eachWayPlacesFraction?.denominator || "",
              places: eachWayPlaces || "",
            },
          })
        : undefined;

      return {
        id: combination.id,
        creationTimestamp: combination.creationTimestamp,
        legId,
        odds: buildCombinationOdds(combination, oddsDisplay),
        previousOdds: previousOdds ? formatOdds(previousOdds, oddsDisplay) : undefined,
        stake: combination.stake ? combination.stake : undefined,
        returnsLabel: buildReturnsLabel({ odds, potentialReturns, localization }),
        selection: { title, subtitle },
        icon,
        silkFallbackIconType: silkFallbackType,
        isBonusAvailable: combination.isBonusAvailable,
        isEachWayAvailable,
        isEachWaySelected,
        eachWaySubtitle,
        isPriceBoostAvailable,
        isPriceBoostSelected,
        multiplier: combination.isEachWaySelected ? `${combination.numLines * 2}x` : undefined,
      };
    });
};

export const translateMultiple = (betType: BET_TYPES): string => {
  const key = `I18N.BETSLIP.SBK.MULTIPLE.${betType}_LINES` as const;
  return i18n({ key });
};

/**
 * With state details return a function that takes a combination
 * and maps to BetBuilder
 */
export const combinationToBetBuilder =
  (
    legsMap: BettingState.LegsMap,
    runnersMetadata: RunnersMetadata,
    localization: BetslipLocalization,
    runnersMapBettingState: BettingState.RunnersMap,
  ): ((combination: BettingState.Combination) => BetBuilder) =>
  (combination) => {
    const { id, betType, legs: legsURN } = combination;

    // since the legs are all of the same game, any leg is good to get the metadata from
    const leg = legsMap[legsURN[0]];
    const { runners } = leg;
    const [legRunnerId] = runners;
    const firstRunnerMetadata = runnersMetadata[legRunnerId];

    const selections: BetSelection[] = legsURN.map((legURN) => {
      const runnerID = legsMap[legURN].runners[0];
      const runner = runnersMetadata[runnerID];
      const bettingRunner = runnersMapBettingState[legRunnerId];
      const { title, subtitle } = buildSelection(runner, localization, bettingRunner);
      return {
        id: legURN,
        urn: runner.runnerUrn,
        action: i18n({ key: "I18N.BETSLIP.REMOVE" }),
        title,
        subtitle,
      };
    });

    return {
      id,
      betType: translateMultiple(betType),
      subtitle:
        firstRunnerMetadata.type === "GENERIC" ? firstRunnerMetadata.eventName : firstRunnerMetadata.racing.venue,
      selections,
      selectionsLabel: i18n({
        key: "I18N.BETSLIP.SELECTIONS_COUNT",
        interpolationValues: { numberOfSelections: selections.length },
      }),
    };
  };

export const createAreAllCombinationsClosedOrSuspendedSelector = () =>
  createSelector(
    [getSportsbookBettingRunners, getSportsbookBettingImplyRunnerFailures],
    (allRunners, failedRunners) => {
      const allRunnersIds = Object.keys(allRunners).sort();
      const failedRunnersIds = Object.keys(failedRunners).sort();

      if (
        allRunnersIds.length !== failedRunnersIds.length &&
        allRunnersIds.some((id, index) => id !== failedRunnersIds[index])
      ) {
        return false;
      }

      const areAllCombinationsClosedOrSuspended = Object.values(failedRunners).every(
        (runnerFailures) => hasAnyMarketClosedFailure(runnerFailures) || hasAnyMarketSuspendedFailure(runnerFailures),
      );

      return areAllCombinationsClosedOrSuspended;
    },
  );
